import { useMemo } from "react";
import { Card, IconButton, Box, Skeleton } from "@mui/material";
import { DataGrid, GridColDef, GridSortModel, GridPaginationModel } from "@mui/x-data-grid";
import { PAGE_LIMIT } from "../../utils/consts";
import { fetchDocumentUrlAndOpenDialog } from "../../services/sharepoint.service";
import { ArrowForward, FileOpen } from '@mui/icons-material';

type ExpertsTableProps = {
  data: any[],
  page: number,
  setPage: Function,
  pageTotal: number,
  setSortConfig: Function,
  sortConfig: { key: string, direction: string }
  handleOpenUpdateDialog: Function,
  loading?: boolean,
}

// The "Years of Experience" column reads its cell value from `years_of_work_experience`,
// but the backend's server-side sort expects the key `years_of_working_experience` - this
// mismatch already existed in the old hand-rolled <TableSortLabel> table, so it's preserved
// here rather than "fixed", to avoid changing what gets sent to experts/filterdata|expertsTable.
const GRID_FIELD_TO_SORT_KEY: Record<string, string> = {
  years_of_work_experience: 'years_of_working_experience',
};
const SORT_KEY_TO_GRID_FIELD: Record<string, string> = {
  years_of_working_experience: 'years_of_work_experience',
};

// A handful of stacked bars stands in for a full skeleton grid - simple, and avoids pulling in
// DataGrid's row/column skeleton machinery just for the initial-load placeholder.
function SkeletonOverlay() {
  return (
    <Box sx={{ p: 2 }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 1, borderRadius: 1 }} />
      ))}
    </Box>
  );
}

function ExpertsTable({
  data,
  page,
  setPage,
  pageTotal,
  setSortConfig,
  sortConfig,
  handleOpenUpdateDialog,
  loading = false,
}: ExpertsTableProps) {
  const accessToken = localStorage.getItem('accessToken') || '';

  const columns: GridColDef[] = useMemo(() => [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'keywords', headerName: 'Experience', flex: 1.3, minWidth: 180 },
    { field: 'Age', headerName: 'Age', width: 80 },
    { field: 'years_of_work_experience', headerName: 'Years of Experience', width: 160 },
    { field: 'nationality', headerName: 'Nationality', width: 130 },
    { field: 'languages', headerName: 'Languages', flex: 1, minWidth: 130 },
    { field: 'regions', headerName: 'Regions', flex: 1, minWidth: 130 },
    { field: 'donors', headerName: 'Donors', flex: 1, minWidth: 130 },
    {
      field: 'referencesWorkedCount', headerName: '#Projects', width: 150,
      valueGetter: (params) => {
        const total = params.row.referencesWorkedCount ?? 0;
        const viaPartner = params.row.referencesViaPartnerCount ?? 0;
        return viaPartner ? `${total} (${viaPartner} via partner)` : total;
      },
    },
    { field: 'viaPartners', headerName: 'Via partner', flex: 1, minWidth: 150, valueGetter: (params) => params.row.viaPartners ?? '' },
    { field: 'positions', headerName: 'Recent Project Positions', flex: 1.3, minWidth: 200 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
    { field: 'cvYear', headerName: 'latest CV Year', width: 130 },
    {
      field: 'consent', headerName: 'Consent', width: 100, sortable: true,
      valueGetter: (params) => (params.row.consent === true ? 'Yes' : 'No'),
    },
    {
      field: 'viewCv', headerName: 'View Latest CV', width: 130, sortable: false, filterable: false,
      renderCell: (params) => (
        <IconButton onClick={() => fetchDocumentUrlAndOpenDialog(params.row.cvlink, accessToken)}>
          <FileOpen />
        </IconButton>
      ),
    },
    {
      field: 'viewDetails', headerName: 'View Details', width: 110, sortable: false, filterable: false,
      renderCell: (params) => (
        <IconButton onClick={() => handleOpenUpdateDialog(params.row.expertID)}>
          <ArrowForward />
        </IconButton>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [accessToken]);

  const sortModel: GridSortModel = sortConfig.key
    ? [{ field: SORT_KEY_TO_GRID_FIELD[sortConfig.key] || sortConfig.key, sort: sortConfig.direction as 'asc' | 'desc' }]
    : [];

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length === 0) return; // mirrors the old TableSortLabel, which always keeps a sort active
    const { field, sort } = model[0];
    setSortConfig({ key: GRID_FIELD_TO_SORT_KEY[field] || field, direction: sort || 'asc' });
  };

  const paginationModel: GridPaginationModel = { page: Math.max(page - 1, 0), pageSize: PAGE_LIMIT };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPage(model.page + 1);
  };

  return (
    <Card>
      <div style={{ height: 700, width: '100%' }}>
        <DataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row.expertID}
          loading={loading}
          sortingMode="server"
          sortModel={sortModel}
          sortingOrder={['asc', 'desc']} // no third "no sort" click state, same as the old TableSortLabel toggle
          onSortModelChange={handleSortModelChange}
          paginationMode="server"
          rowCount={pageTotal}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[PAGE_LIMIT]}
          disableColumnFilter
          disableRowSelectionOnClick
          slots={{
            loadingOverlay: SkeletonOverlay,
          }}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
            },
          }}
        />
      </div>
    </Card>
  );
}

export default ExpertsTable;
